import { useRef } from 'react';
import classNames from 'classnames';
import './ScheduleBadge.scss';

export interface ScheduleBadgeProps {
  /** sets visibility/color of red dot. default false */
  isLive?: boolean;
  /** content text */
  label?: string;
  /** small or large size. default small */
  size?: 'small' | 'large';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  /** optional override. default 0 */
  tabIndex?: number;
}

/**
 * ScheduleBadge component
 * @param isLive - sets visibility/color of red dot. default false
 * @param label - content text
 * @param size - small or large size. default small
 * @param onClick - optional onClick event e.g. for jump to live
 * @param className - optional style class
 * @param tabIndex - optional override. default 0
 */
export const ScheduleBadge: React.FC<ScheduleBadgeProps> = ({
  label,
  isLive,
  onClick,
  size = 'small',
  className,
  tabIndex = 0,
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      ref?.current?.click();
    }
  };

  const showDot = isLive || size === 'large';

  return (
    <button
      className={classNames(
        'vds-schedule-badge',
        `vds-schedule-badge--${size}`,
        `vds-badge-schedule-${size}`, // typography class
        className,
      )}
      onClick={onClick}
      ref={ref}
      tabIndex={tabIndex}
      onKeyDown={handleKeyDown}
      type="button">
      {showDot && (
        <span
          className={classNames('vds-schedule-badge__dot', isLive && 'vds-schedule-badge__live')}
        />
      )}
      <span className="vds-schedule-badge__label">{label}</span>
    </button>
  );
};
